
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ConnectedAccount } from '@/components/settings/credenciales/types';

interface AcquirersContextType {
  connections: ConnectedAccount[];
  isLoading: boolean;
  error: string | null;
  addConnection: (account: ConnectedAccount) => void;
  removeConnection: (id: string) => void;
  updateConnection: (id: string, fields: Partial<ConnectedAccount>) => void;
  refresh: () => Promise<void>;
}

const AcquirersContext = createContext<AcquirersContextType | null>(null);

export function AcquirersProvider({ children }: { children: ReactNode }) {
  const [connections, setConnections] = useState<ConnectedAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setError(null);
      const { httpClient } = await import('@/lib/http-client');
      const { getEndpointUrl } = await import('@/lib/api-config');
      const url = getEndpointUrl('acquirerConnections', 'list');
      const data = await httpClient.get<{ connections: ConnectedAccount[] }>(url, { shouldCache: false });
      setConnections(data.connections ?? []);
    } catch {
      setError('No se pudieron cargar las conexiones. Intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    void load();
  }, []);

  const addConnection = (account: ConnectedAccount) => {
    setConnections((prev) => [...prev, account]);
  };

  const removeConnection = (id: string) => {
    setConnections((prev) => prev.filter((c) => c.id !== id));
  };

  const updateConnection = (id: string, fields: Partial<ConnectedAccount>) => {
    setConnections((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...fields } : c))
    );
  };

  return (
    <AcquirersContext.Provider value={{ connections, isLoading, error, addConnection, removeConnection, updateConnection, refresh: load }}>
      {children}
    </AcquirersContext.Provider>
  );
}

export function useAcquirers() {
  const ctx = useContext(AcquirersContext);
  if (!ctx) throw new Error('useAcquirers must be used within AcquirersProvider');
  return ctx;
}
